import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * A custom hook that scrolls to the top of the page when the location changes.
 * This ensures consistent navigation behavior across the application.
 * 
 * @param {Object} options - Options for controlling the scroll behavior
 * @param {boolean} options.smooth - Whether to use smooth scrolling (default: false)
 * @param {number} options.delay - Optional delay in ms before scrolling (default: 0)
 */
export const useScrollToTop = ({ 
  smooth = false, 
  delay = 0 
}: { 
  smooth?: boolean; 
  delay?: number 
} = {}) => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Don't scroll to top if there's a hash (anchor link)
    if (hash) return;

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: smooth ? 'smooth' : 'auto'
      });
    };

    if (delay) {
      const timeoutId = setTimeout(scrollToTop, delay);
      return () => clearTimeout(timeoutId);
    } else {
      scrollToTop();
    }
  }, [pathname, hash, smooth, delay]);
}; 